import { Alert, Link, Paragraph } from "@george-labs.com/design-system"
import dayjs from "../../utils/dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

interface IAppointmentTimeDurationProps {
  startTime: string
  duration: number
}

export const AppointmentTimeDuration: React.FC<
  IAppointmentTimeDurationProps
> = ({ startTime, duration }) => {
  const start = dayjs(startTime, "HH:mm")
  const end = start.add(duration, "m")

  return (
    <>
      {start.format("HH:mm")} - {end.format("HH:mm")}
    </>
  )
}

interface IAppointmentRetryAlertProps {
  text: string
  retryButtonClicked: () => void
}

export const AppointmentRetryAlert: React.FC<IAppointmentRetryAlertProps> = ({
  text,
  retryButtonClicked,
}) => {
  const { t } = useTranslation()

  return (
    <Alert
      heading={t("offers.appointment.errorTitle")}
      variant={Alert.VARIANT.WARNING}
    >
      {t(text)}
      <Paragraph style={{ textAlign: "end" }}>
        <Link
          onClick={(e) => {
            e.preventDefault()
            retryButtonClicked()
          }}
        >
          {t("offers.appointment.link")}
        </Link>
      </Paragraph>
    </Alert>
  )
}

export const generateIcs = (
  title: string,
  description: string,
  location: string,
  startDateTime: string,
  durationMinutes: number,
) => {
  const pad = (num: number) => String(num).padStart(2, "0")

  const formatDate = (date: Date) => {
    return (
      date.getUTCFullYear() +
      pad(date.getUTCMonth() + 1) +
      pad(date.getUTCDate()) +
      "T" +
      pad(date.getUTCHours()) +
      pad(date.getUTCMinutes()) +
      "00Z"
    )
  }

  const dateStart = new Date(startDateTime)
  const dateEnd = new Date(dateStart.getTime() + durationMinutes * 60000)

  const uid = `${Date.now()}@yourapp.com`

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//YourApp//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${formatDate(new Date())}`,
    `DTSTART:${formatDate(dateStart)}`,
    `DTEND:${formatDate(dateEnd)}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${description || ""}`,
    `LOCATION:${location || ""}`,
    "STATUS:CONFIRMED",
    "SEQUENCE:0",
    "TRANSP:OPAQUE",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n")
}
